import {
  faDownload,
  faMagnifyingGlass,
  faPlus,
  faSliders,
  faTableColumns,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mantine/core';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';

import { Breadcrumbs, Button, Input, PageHeader, Table, Toolbar } from '@feathr/components';

import invitesCampaignColumns from '../columns/invitesCampaignColumns';
import PrototypeDoneModal from '../components/PrototypeDoneModal';
import type { ISampleInvitesCampaign } from '../sampleData';
import { sampleInvitesCampaigns } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';

import * as shellStyles from '../Shell/PrototypeShell.css';

function PartnerMarketingPage(): JSX.Element {
  const { t } = useTranslation();
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Partner Marketing'), defaultPath: '/partner-marketing/invites' },
    t('Invites Campaigns'),
    '/partner-marketing/invites',
  );

  const columns = useMemo(() => invitesCampaignColumns({ base, t }), [base, t]);

  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) {
      return sampleInvitesCampaigns;
    }
    return sampleInvitesCampaigns.filter((c) => c.name.toLowerCase().includes(lower));
  }, [search]);

  const filterElements = [
    <Box key={'search'} w={320}>
      <Input
        isClearable={true}
        onChange={(next?: string): void => setSearch(next ?? '')}
        placeholder={t('Search by name…')}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        type={'text'}
        value={search}
      />
    </Box>,
    <Toolbar align={'right'} key={'filters'}>
      <Button prefix={<FontAwesomeIcon icon={faSliders} />} type={'secondary'}>
        {t('Filters')}
      </Button>
      <Button prefix={<FontAwesomeIcon icon={faTableColumns} />} type={'secondary'}>
        {t('Customize columns')}
      </Button>
    </Toolbar>,
  ];

  const actions = (
    <Toolbar>
      <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
        {t('Export as CSV')}
      </Button>
      <Button
        onClick={(): void => setDoneOpen(true)}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {t('Create')}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader
        actions={actions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={t('Invites Campaigns')}
      />
      <div className={shellStyles.body}>
        <Table<ISampleInvitesCampaign>
          columns={columns}
          filterElements={filterElements}
          filterLabel={<></>}
          idKey={'id'}
          isPaginated={false}
          items={filtered}
        />
      </div>

      <PrototypeDoneModal
        description={t(
          'The invites-campaign builder opens at this point. The prototype ends here.',
        )}
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={t('Create invites campaign')}
      />
    </>
  );
}

export default PartnerMarketingPage;
